import React from "react";
import type { Intent, Skin } from "constants/DefaultTheme";
import { BlueprintButtonIntentsCSS } from "constants/DefaultTheme";
import styled, { css } from "styled-components";
import type {
  Intent as BlueprintIntent,
  IconName,
  MaybeElement,
  IButtonProps,
  IAnchorButtonProps,
} from "@blueprintjs/core";
import {
  AnchorButton as BlueprintAnchorButton,
  Button as BlueprintButton,
} from "@blueprintjs/core";
import type { Direction } from "utils/helpers";
import { Directions } from "utils/helpers";
import { omit } from "lodash";

const outline = css`
  &&&&&& {
    border-width: 1px;
    border-style: solid;
  }
`;

const buttonStyles = css<Partial<ButtonProps>>`
  ${BlueprintButtonIntentsCSS}
  &&&& {
    border-radius: 0;
    background: ${(props) =>
      props.filled || props.outline ? "inherit" : "transparent"};
    border-radius: ${({ borderRadius }) => borderRadius};
    box-shadow: ${({ boxShadow }) => `${boxShadow}`} !important;
    width: ${(props) => (props.fluid ? "100%" : "auto")};
    height: 100%;
    padding: 0 10px;
  }
  &&&&&& {
    &.bp3-button span {
      font-weight: ${(props) => (props.skin !== undefined ? 400 : 700)};
    }
    .bp3-icon svg {
      width: ${(props) => (props.skin !== undefined ? 14 : 16)}px;
      height: ${(props) => (props.skin !== undefined ? 14 : 16)}px;
    }
    &.bp3-button {
      display: flex;
      justify-content: ${(props) =>
        props.skin === undefined
          ? "center"
          : props.iconAlignment === Directions.RIGHT
          ? "space-between"
          : "flex-start"};
    }
  }
  ${(props) => (props.outline ? outline : "")}
`;
const StyledButton = styled((props: IButtonProps & Partial<ButtonProps>) => (
  <BlueprintButton
    {...omit(props, [
      "borderRadius",
      "boxShadow",
      "boxShadowColor",
      "iconAlignment",
      "fluid",
      "filled",
      "outline",
    ])}
  />
))`
  ${buttonStyles}
`;
const StyledAnchorButton = styled(
  (props: IAnchorButtonProps & Partial<ButtonProps>) => (
    <BlueprintAnchorButton
      {...omit(props, ["iconAlignment", "fluid", "filled", "outline"])}
    />
  ),
)`
  ${buttonStyles}
`;

export interface ButtonProps {
  outline?: boolean;
  filled?: boolean;
  intent?: Intent;
  text?: string;
  onClick?: (e?: React.MouseEvent<HTMLElement>) => void;
  href?: string;
  icon?: string | MaybeElement;
  iconAlignment?: Direction;
  loading?: boolean;
  disabled?: boolean;
  size?: "large" | "small";
  type?: "button" | "submit" | "reset";
  className?: string;
  fluid?: boolean;
  skin?: Skin;
  target?: string;
  borderRadius?: string;
  boxShadow?: string;
  boxShadowColor?: string;
}

export const Button = (props: ButtonProps) => {
  const icon: IconName | undefined =
    props.icon &&
    (props.iconAlignment === Directions.LEFT ||
      props.iconAlignment === undefined)
      ? (props.icon as IconName)
      : undefined;
  const rightIcon: IconName | undefined =
    props.icon && props.iconAlignment === Directions.RIGHT
      ? (props.icon as IconName)
      : undefined;

  const baseProps = {
    text: props.text,
    minimal: !props.filled,
    outline: !!props.outline,
    filled: !!props.filled,
    intent: props.intent as BlueprintIntent,
    large: props.size === "large",
    small: props.size === "small",
    loading: props.loading,
    disabled: props.disabled,
    type: props.type,
    className: props.className,
    fluid: !!props.fluid,
    skin: props.skin,
    iconAlignment: props.iconAlignment ? props.iconAlignment : undefined,
  };
  if (props.href) {
    return (
      <StyledAnchorButton
        icon={icon}
        rightIcon={rightIcon}
        {...baseProps}
        href={props.href}
        target={props.target}
      />
    );
  } else
    return (
      <StyledButton
        borderRadius={props.borderRadius}
        boxShadow={props.boxShadow}
        boxShadowColor={props.boxShadowColor}
        icon={icon}
        rightIcon={rightIcon}
        {...baseProps}
        onClick={props.onClick}
      />
    );
};

export default Button;
